import React, { useState, useEffect } from 'react';
import { Link, useLocation, useNavigate } from 'react-router-dom';
import Icon from '../AppIcon';
import Button from './Button';
import Image from 'components/AppImage';
import { useUserProfile } from 'features/auth/hooks/useUserProfile';
import { useAuth } from 'contexts/AuthContext';


const Sidebar = ({ className = '', onToggle }) => {
  const [isCollapsed, setIsCollapsed] = useState(false);
  const location = useLocation();
  const navigate = useNavigate();
  const navRef = React.useRef(null);

  // AuthContext's `user` is read synchronously from localStorage at app
  // bootstrap (see AuthContext.jsx) and is already resolved by the time this
  // component mounts — ProtectedRoute blocks rendering until its `loading`
  // flag clears. That makes it the correct source for the FIRST render:
  // no network round-trip, no "role not known yet" window. useUserProfile()
  // (a live API call) is kept only as a secondary sync, so a role changed
  // mid-session (e.g. a super-admin edits someone's permissions while they're
  // logged in) still gets picked up — but it only ever refines an
  // already-correct menu, it's never what the first render waits on.
  const { user: authUser, loading: isAuthLoading } = useAuth();
  const { data: profileData } = useUserProfile();
  const freshUser = profileData?.data;
  const user = freshUser || authUser;
  const isRoleKnown = !isAuthLoading && !!user?.role?.name;


  const navigationItems = [

    {
      label: 'Executive Dashboard',
      path: '/executive-dashboard',
      icon: 'BarChart3',
      description: 'Business intelligence overview',
      restrictedRoles: [
  "delivery_manager",
  "category_manager",
  "operations_manager",
  "travel_manager",
  // "finance_manager",
]
    },
    {
      label: 'Admins & Sub-Admins',
      path: '/admins',
      icon: 'ShieldUser',
      description: 'Manage admin access and permissions',
      restrictedRoles: [
        "delivery_manager",
        "category_manager",
        "operations_manager",
        "travel_manager",
        "finance_manager"
      ]
    },
    {
      label: 'Main category',
      path: '/category/main',
      icon: 'FolderTree',
      description: 'Primary category management',
      restrictedRoles: [
  'delivery_manager',
  'travel_manager',
  'operations_manager',
  'finance_manager'
]
    },
    {
      label: 'Sub category',
      path: '/category/sub',
      icon: 'FolderOpen',
      description: 'Nested category organization',
      restrictedRoles: [
  "delivery_manager",
  "travel_manager",
  "operations_manager",
  "finance_manager",
]
    },
    {
      label: 'Packages',
      path: '/packages',
      icon: 'Package',
      description: 'Package management',
      restrictedRoles: [
  "delivery_manager",
  "category_manager",
  "operations_manager",
  // 'travel_manager',
  "finance_manager",
]
    },
    {
      label: 'Category Management',
      path: '/services',
      icon: 'Wrench',
      description: 'Manage service categories',
      restrictedRoles: [
  "delivery_manager",
  "travel_manager",
  "operations_manager",
  "finance_manager",
]
    },
    {
      label: 'Service Catalog',
      path: '/service-catalog',
      icon: 'ClipboardList',
      description: 'Curate the admin service catalog',
      restrictedRoles: [
  "delivery_manager",
  "travel_manager",
  "operations_manager",
  "finance_manager",
]
    },
    {
      label: 'Properties',
      path: '/properties',
      icon: 'Building',
      description: 'Property management',
      restrictedRoles: [
  "delivery_manager",
  "category_manager",
  "operations_manager",
  "travel_manager",
  "finance_manager",
]
    },
    {
      label: 'Property Attributes',
      path: '/property-attributes',
      icon: 'Layers',
      description: 'Manage Amenities, Rooms, Overlooking & Property Types',
      restrictedRoles: [
  "delivery_manager",
  "category_manager",
  "operations_manager",
  "travel_manager",
  "finance_manager",
]
    },
    {
      label: 'Tours',
      path: '/tours',
      icon: 'MapPin',
      description: 'Tour package management',
      restrictedRoles: [
  "delivery_manager",
  "category_manager",
  "operations_manager",
  "finance_manager",
]
    },
    // {
    //   label: 'Role',
    //   path: '/role',
    //   icon: 'UserCog',
    //   description: 'Role management',
    // },
    // {
    //   label: 'Permission',
    //   path: '/permission',
    //   icon: 'Lock',
    //   description: 'Permission management',
    // },
    {
      label: 'Service Provider',
      path: '/sellers',
      icon: 'Store',
      description: 'Manage companies, freelancers and staff',
      restrictedRoles: [
        "delivery_manager",
        "category_manager",
        "operations_manager",
        "travel_manager",
        "finance_manager"
      ]
    },
    {
      label: 'Customers',
      path: '/customers',
      icon: 'UserCheck',
      description: 'Manage customers and their profiles',
      restrictedRoles: [
        "delivery_manager",
        "category_manager",
        "operations_manager",
        "travel_manager",
        "finance_manager"
      ]
    },
    {
      label: 'Support Tickets',
      path: '/support-tickets',
      icon: 'Headphones',
      description: 'Manage customer support tickets',
    },

    {
      label: 'Support Chat',
      path: '/support-chat',
      icon: 'MessageCircle',
      description: 'Chat with users',
    },
    {
      label: 'SOS Logs',
      path: '/sos-logs',
      icon: 'AlertTriangle',
      description: 'Emergency SOS alerts and logs',
    },
    {
      label: 'Bookings',
      path: '/bookings',
      icon: 'Calendar',
      description: 'Manage service bookings',
      restrictedRoles: ["category_manager", "finance_manager"]
    },
     {
      label: 'Appointments',
      path: '/appointment',
      icon: 'Calendar',
      description: 'Property Appointments',
      restrictedRoles: [
      "delivery_manager",
      "travel_manager",
      // 'operations_manager',
      "category_manager",
      "finance_manager",
    ]
    },
    {
      label: 'Menu Management',
      path: '/menu',
      icon: 'UtensilsCrossed',
      description: 'Manage food menu',
      restrictedRoles: [
          "category_manager",
          "travel_manager",
          "operations_manager",
          "finance_manager",
        ]
    },
    {
      label: 'Jobs',
      path: '/jobs',
      icon: 'Briefcase',
      description: 'Manage job postings and applications',
      restrictedRoles: [
        "delivery_manager",
        "category_manager",
        "operations_manager",
        "travel_manager",
        "finance_manager"
      ]
    },
    // {
    //   label: 'Catalogue & Geo',
    //   path: '/catalogue-geo-management',
    //   icon: 'MapPin',
    //   description: 'Category and location management',
    // },
    // {
    //   label: 'User & Partner',
    //   path: '/user-partner-management',
    //   icon: 'UserCheck',
    //   description: 'User and partner management',
    // },
    {
      label: 'Banner Management',
      path: '/content-management-system',
      icon: 'FileText',
      description: 'Manage application banners',
      restrictedRoles: [
      "delivery_manager",
      "category_manager",
      "operations_manager",
      "travel_manager",
      'finance_manager'
    ]
    },
    {
      label: 'CMS Pages',
      path: '/cms',
      icon: 'FileEdit',
      description: 'Manage Terms, Privacy, About Us pages',
      restrictedRoles: [
        "delivery_manager",
        "travel_manager",
        "operations_manager",
        "category_manager",
        "finance_manager",
      ]
    },
    {
      label: 'Global Configauration',
      path: '/global-configuration',
      icon: 'SlidersHorizontal',
      description: 'Manage global settings',
      restrictedRoles: [
            "delivery_manager",
            "travel_manager",
            "operations_manager",
            "category_manager",
            "finance_manager",
          ]
    },
    {
      label: 'Offer',
      path: '/offer',
      icon: 'Tag',
      description: 'Offer & Discount management',
      restrictedRoles: [
  "delivery_manager",
  "category_manager",
  "operations_manager",
  "travel_manager",
  "finance_manager",
]
    },

    {
  label: 'Review Management',
  path: '/reviews',
  icon: 'Star',
  description: 'Manage customer and seller reviews',
  restrictedRoles: [
    "delivery_manager",
    "travel_manager",
    "category_manager",
    "operations_manager",
    "finance_manager",
  ]
},
    //  {
    //   label: 'Socials',
    //   path: '/social',
    //   icon: 'Link',
    //   description: 'Social media accounts management',
    // },

    
    // {
    //   label: 'subscription-management',
    //   path: '/subscription-management',
    //   icon: 'Layers',
    //   description: 'Subscription management',
    // },
    // {
    //   label: 'Verifications & Approvals',
    //   path: '/verifications-approvals',
    //   icon: 'Shield',
    //   description: 'Listing verification hub',
    // },
    // {
    //   label: 'Order Intelligence',
    //   path: '/order-intelligence-center',
    //   icon: 'ShoppingCart',
    //   description: 'Real-time order monitoring and analytics',
    // },
    
    // {
    //   label: 'Operations Monitor',
    //   path: '/operations-monitor',
    //   icon: 'Activity',
    //   description: 'Real-time system monitoring',
    // },
    // {
    //   label: 'Partner Analytics',
    //   path: '/partner-analytics-hub',
    //   icon: 'Users',
    //   description: 'Partner relationship management',
    // },
    // {
    //   label: 'Revenue Analytics',
    //   path: '/revenue-analytics',
    //   icon: 'TrendingUp',
    //   description: 'Financial intelligence',
    // },
  ];

  // Fails CLOSED: until the role is actually known, show nothing rather than
  // everything. Previously this was `!restrictedRoles.includes(user?.role?.name)`,
  // which is fail-OPEN — `restrictedRoles.includes(undefined)` is always false,
  // so every item (including ones that should be hidden) passed the filter for
  // the entire window before the role loaded. That was the flash.
  const visibleNavigationItems = isRoleKnown
    ? navigationItems.filter(
        (item) => !item?.restrictedRoles?.includes(user?.role?.name)
      )
    : [];

  const isActivePath = (path) => {
  if (path === '/') return location.pathname === '/';
  return location.pathname === path || location.pathname.startsWith(path + '/');
};

  const toggleSidebar = () => {
    const newCollapsed = !isCollapsed;
    setIsCollapsed(newCollapsed);
    if (onToggle) {
      onToggle(newCollapsed);
    }
  };

  const handleNavigation = (path) => {
    navigate(path);
  };

  // Notify parent component when collapsed state changes
  useEffect(() => {
    if (onToggle) {
      onToggle(isCollapsed);
    }
  }, [isCollapsed, onToggle]);

  // Save scroll position to localStorage when scrolling
  useEffect(() => {
    const navElement = navRef.current;
    if (!navElement) return;

    const handleScroll = () => {
      localStorage.setItem(
        'sidebarScrollPosition',
        navElement.scrollTop.toString(),
      );
    };

    navElement.addEventListener('scroll', handleScroll);
    return () => navElement.removeEventListener('scroll', handleScroll);
  }, []);

  // Restore scroll position on mount and when location changes
  useEffect(() => {
    const navElement = navRef.current;
    if (!navElement) return;

    const savedPosition = localStorage.getItem('sidebarScrollPosition');
    if (savedPosition) {
      navElement.scrollTop = parseInt(savedPosition, 10);
    }
  }, [location.pathname]);

  return (
    <aside
      className={`fixed left-0 top-0 bottom-0 z-50 bg-card border-r border-border transition-all duration-300 ${
        isCollapsed ? 'w-20 small-sidebar' : 'w-64'
      } ${className}`}
    >
      <div className='flex flex-col h-full'>
        {/* Logo Section */}
        <div className={`relative flex items-center h-16 border-b border-border ${isCollapsed ? 'justify-center px-2' : 'justify-between px-4'}`}>
          <Link
            to='/executive-dashboard'
            className={`flex flex-col items-center space-x-3 min-w-0 ${isCollapsed ? '' : 'w-full'}`}
          >
            <div
              className={`flex items-center justify-center rounded-lg flex-shrink-0 overflow-hidden ${
                isCollapsed ? 'w-9 h-9' : 'w-14 h-10'
              }`}
            >
              <Image
                src={isCollapsed ? '/assets/images/logo-mark.svg' : '/assets/images/logo.svg'}
                alt='Qkly Logo'
                className='w-full h-full'
              />
            </div>
            {!isCollapsed && (
              <div className='min-w-0 w-full pr-3 text-center'>
                <h1 className='text-sm font-semibold text-foreground truncate text-white'>
                  {user?.role?.name
    ?.replace(/_/g, ' ')
    .replace(/\b\w/g, (char) => char.toUpperCase())}
                </h1>
              </div>
            )}
          </Link>

        </div>

        {/* Floating toggle button, anchored beside the logo in both states */}
        <button
          onClick={toggleSidebar}
          className='absolute -right-3 top-5 flex items-center justify-center w-8 h-8 rounded-full bg-white text-black shadow-lg hover:bg-gray-100 transition-colors z-10'
        >
          <Icon name={isCollapsed ? 'ChevronRight' : 'ChevronLeft'} size={20} />
        </button>

        {/* Navigation */}
        <nav ref={navRef} className='flex-1 py-4 overflow-y-auto overflow-x-hidden no-scrollbar'>
          <div className='space-y-0.5 px-2.5'>
            {visibleNavigationItems?.map((item) => {
              const active = isActivePath(item?.path);
              return (
                <Link
                  key={item?.path}
                  to={item?.path}
                  className={`nav-item flex items-center py-1.5 rounded-full text-sm font-medium transition-hover group relative border-l-2 ${
                    isCollapsed ? 'justify-center px-0 border-l-0' : 'space-x-3 pl-2.5 pr-3'
                  } ${active ? 'nav-item-active border-white' : 'border-transparent hover:bg-white/10'}`}
                  title={isCollapsed ? item?.label : ''}
                >
                  <span
                    className={`flex items-center justify-center w-8 h-8 rounded-full flex-shrink-0 transition-colors ${
                      active ? 'bg-white text-primary' : 'bg-white/15 text-white group-hover:bg-white/25'
                    }`}
                  >
                    <Icon name={item?.icon} size={16} />
                  </span>
                  {!isCollapsed && (
                    <div className='min-w-0 flex-1'>
                      <div className='truncate'>{item?.label}</div>
                      <div className={`text-xs truncate ${active ? 'text-primary/70' : 'text-white/70'}`}>
                        {item?.description}
                      </div>
                    </div>
                  )}

                  {/* Tooltip for collapsed state */}
                  {isCollapsed && (
                    <div className='absolute left-full top-1/2 -translate-y-1/2 ml-2 px-3 py-2 bg-popover border border-border rounded-md text-sm text-foreground opacity-0 invisible group-hover:opacity-100 group-hover:visible transition-all duration-200 z-50 whitespace-nowrap'>
                      <div className='font-medium'>{item?.label}</div>
                      <div className='text-xs text-muted-foreground'>
                        {item?.description}
                      </div>
                      <div className='absolute top-1/2 -translate-y-1/2 left-0 -translate-x-1 w-2 h-2 bg-popover border-l border-b border-border rotate-45'></div>
                    </div>
                  )}
                </Link>
              );
            })}
          </div>
        </nav>

        {/* Footer Actions */}
        <div className='border-t border-border p-3 footer-btn'>
          <div className='space-y-0.5'>
            <button
              className={`nav-item flex items-center py-1.5 rounded-full text-sm font-medium transition-hover w-full group relative border-l-2 ${
                isCollapsed ? 'justify-center px-0 border-l-0' : 'space-x-3 pl-2.5 pr-3'
              } ${
                isActivePath('/settings')
                  ? 'nav-item-active border-white'
                  : 'border-transparent hover:bg-white/10'
              }`}
              onClick={() => handleNavigation('/settings')}
              title={isCollapsed ? 'Settings' : ''}
            >
              <span
                className={`flex items-center justify-center w-8 h-8 rounded-full flex-shrink-0 transition-colors ${
                  isActivePath('/settings')
                    ? 'bg-white text-primary'
                    : 'bg-white/15 text-white group-hover:bg-white/25'
                }`}
              >
                <Icon name='Settings' size={16} />
              </span>
              {!isCollapsed && <span>Settings</span>}

              {isCollapsed && (
                <div className='absolute left-full top-1/2 -translate-y-1/2 ml-2 px-3 py-2 bg-popover border border-border rounded-md text-sm text-foreground opacity-0 invisible group-hover:opacity-100 group-hover:visible transition-all duration-200 z-50 whitespace-nowrap'>
                  Settings
                  <div className='absolute top-1/2 -translate-y-1/2 left-0 -translate-x-1 w-2 h-2 bg-popover border-l border-b border-border rotate-45'></div>
                </div>
              )}
            </button>
          </div>
        </div>
      </div>
    </aside>
  );
};

export default Sidebar;
